import React, { useState, useEffect } from 'react';
import { Card, Spinner, Badge, Alert, Button, Modal, Container } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { API_URL } from "../../utils/constant";
import { faSearch, faSync, faExternalLinkAlt, faImage, faLink } from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';
import Pagination from '../Pagination/Pagination';

const HacktivismTracker = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [threats, setThreats] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [totalItems, setTotalItems] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedThreat, setSelectedThreat] = useState(null);
    const [showModal, setShowModal] = useState(false);
    

    const fetchThreats = async () => {
        setLoading(true);
        try {
            const url = `${API_URL}/threat-intel/hacktivism/threats?page=${currentPage}&limit=${itemsPerPage}&search=${searchTerm}`;
            
            const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('session')}`,
                    'Content-Type': 'application/json'
                }
            });

            const result = await response.json();
            
            if (response.status !== 200) {
                throw new Error(result.error || 'Failed to fetch threats');
            }

            setThreats(result.threats);
            setTotalItems(result.pagination.totalItems);
        } catch (err) {
            setError(err.message);
            // console.error removed
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchThreats();
    }, [currentPage, itemsPerPage]);

    const handleSearch = (e) => {
        e.preventDefault();
        setCurrentPage(1);
        fetchThreats();
    };

    const handleShowDetails = (threat) => {
        setSelectedThreat(threat);
        setShowModal(true);
    };

    const renderDetailsModal = () => {
        if (!selectedThreat) return null;

        return (
            <Modal
                show={showModal}
                onHide={() => setShowModal(false)}
                size="lg"
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>{selectedThreat.title}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-3">
                        <h6>Content:</h6>
                        <p>{selectedThreat.content || 'No content available'}</p>
                    </div>

                    {selectedThreat.published_url && (
                        <div className="mb-3">
                            <h6>Published URL:</h6>
                            <a 
                                href={selectedThreat.published_url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-primary"
                            >
                                {selectedThreat.published_url} <FontAwesomeIcon icon={faExternalLinkAlt} size="sm" />
                            </a>
                        </div>
                    )}

                    {selectedThreat.screenshots && selectedThreat.screenshots.length > 0 && (
                        <div className="mb-3">
                            <h6>Screenshots:</h6>
                            <div className="row">
                                {selectedThreat.screenshots.map((screenshot, index) => (
                                    <div key={index} className="col-md-6 mb-2">
                                        <img
                                            src={screenshot}
                                            alt={`Screenshot ${index + 1}`}
                                            className="img-fluid rounded"
                                            style={{ maxHeight: '300px' }}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowModal(false)}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    };

    const renderThreatsTable = () => {
        if (!threats || threats.length === 0) {
            return (
                <Alert variant="info">
                    No hacktivism threats found matching your search.
                </Alert>
            );
        }

        return (
            <div className="table-responsive">
                <table className="table table-custom spacing5 mb-0">
                    <thead>
                        <tr>
                            <th>Title</th>
                            <th>Category</th>
                            <th>Threat Actors</th>
                            <th>Organization</th>
                            <th>Site</th>
                            <th>Published URL</th>
                            <th>Country</th>
                            <th>Industry</th>
                            <th>Date</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {threats.map((threat, index) => (
                            <tr key={index}>
                                <td>{threat.title}</td>
                                <td>
                                    <Badge bg="info" style={{color:"#000", border:"none"}}>
                                        {threat.category || 'N/A'}
                                    </Badge>
                                </td>
                                <td>
                                    <Badge bg="secondary">
                                        {threat.threat_actors}
                                    </Badge>
                                </td>
                                <td>{threat.victim_organization || 'N/A'}</td>
                                <td>
                                    {threat.victim_site && (
                                        <a 
                                            href={`https://${threat.victim_site}`} 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                        >
                                            {threat.victim_site} <FontAwesomeIcon icon={faExternalLinkAlt} size="sm" />
                                        </a>
                                    )}
                                </td>
                                <td>
                                    {threat.published_url && (
                                        <a 
                                            href={threat.published_url} 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            className="text-primary"
                                            title={threat.published_url}
                                        >
                                            <FontAwesomeIcon icon={faLink} /> View Source
                                        </a>
                                    )}
                                </td>
                                <td>{threat.victim_country || 'N/A'}</td>
                                <td>{threat.victim_industry || 'N/A'}</td>
                                <td>
                                    {threat.date ? 
                                        new Date(threat.date).toLocaleDateString() : 
                                        'N/A'
                                    }
                                </td>
                                <td>
                                    <div className="d-flex gap-2">
                                        <Button
                                            variant="link"
                                            size="sm"
                                            onClick={() => handleShowDetails(threat)}
                                            className="p-0"
                                        >
                                            <FontAwesomeIcon icon={faImage} /> View
                                        </Button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    return (
        <Container fluid className="mt-4">
            <Card>
                <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h4 className="welcome-text">Hacktivism Threats</h4>
                        <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => fetchThreats()}
                            disabled={loading}
                        >
                            <FontAwesomeIcon icon={faSync} spin={loading} /> Refresh
                        </Button>
                    </div>

                    <form onSubmit={handleSearch} className="mb-4">
                        <div className="row">
                            <div className="col-md-8">
                                <div className="searchBoxInput p-0">
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Search threats..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>
                            </div>
                            <div className="col-md-4">
                                <Button
                                    type="submit"
                                    variant="primary"
                                    disabled={loading}
                                >
                                    <FontAwesomeIcon icon={faSearch} /> Search
                                </Button>
                            </div>
                        </div>
                    </form>

                    {loading && (
                        <div className="text-center p-4">
                            <Spinner animation="border" />
                        </div>
                    )}

                    {error && (
                        <Alert variant="danger">{error}</Alert>
                    )}

                    {!loading && !error && (
                        <>
                            {renderThreatsTable()}
                            <div className="d-flex justify-content-between align-content-center mt-3">
                                <div className="dataTables_paginate">
                                    {totalItems > 0 && (
                                        <Pagination
                                            className="pagination-bar"
                                            currentPage={currentPage}
                                            totalCount={totalItems}
                                            pageSize={itemsPerPage}
                                            onPageChange={(page) => {
                                                setCurrentPage(page);
                                            }}
                                        />
                                    )}
                                </div>
                                {totalItems > 10 && (
                                    <select
                                        value={itemsPerPage}
                                        onChange={(e) => {
                                            setItemsPerPage(Number(e.target.value));
                                            setCurrentPage(1);
                                        }}
                                        className="form-control limitSelectBox"
                                    >
                                        <option value={10}>10 per page</option>
                                        <option value={25}>25 per page</option>
                                        <option value={50}>50 per page</option>
                                        <option value={100}>100 per page</option>
                                    </select>
                                )}
                            </div>
                        </>
                    )}
                </Card.Body>
            </Card>
            {renderDetailsModal()}
        </Container>
    );
};

export default HacktivismTracker; 